import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Flame, Dumbbell, Wheat, Droplet, Candy, Sparkles, Leaf, Sprout, Clock, Plus, Check, Star, Images, X, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { ADDONS, getRating } from "@/data/menu";
import { useCart } from "@/context/CartContext";

const MacroCell = ({ icon: Icon, label, value, unit }) => (
  <div className="flex items-center gap-2">
    <Icon size={15} className="text-teal-brand shrink-0" />
    <span className="text-xs text-coffee-muted uppercase tracking-wide">{label}</span>
    <span className="ml-auto font-mono text-sm font-semibold text-coffee">{value}{unit}</span>
  </div>
);

export default function NutritionCard({ item, index }) {
  const [selected, setSelected] = useState([]);
  const [lbIndex, setLbIndex] = useState(-1);
  const [showMacros, setShowMacros] = useState(false);
  const touchX = useRef(null);
  const { add, goals } = useCart();

  const gallery = item.gallery?.length ? item.gallery : [item.image];
  const hasGallery = gallery.length > 1;

  const openLb = () => setLbIndex(0);
  const closeLb = () => setLbIndex(-1);
  const step = (dir) => setLbIndex((i) => (i + dir + gallery.length) % gallery.length);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const toggle = (name) => {
    setSelected((s) => (s.includes(name) ? s.filter((n) => n !== name) : [...s, name]));
  };

  const addonTotal = ADDONS.filter((a) => selected.includes(a.name)).reduce((s, a) => s + a.price, 0);
  const { rating, reviews } = getRating(item.name);

  return (
    <motion.article
      data-testid={`menu-card-${index}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col bg-white border border-line rounded-3xl overflow-hidden"
    >
      <div className="relative h-56 overflow-hidden">
        <button
          type="button"
          data-testid={`meal-image-${index}`}
          onClick={openLb}
          className="block w-full h-full cursor-zoom-in"
          aria-label={`View photos of ${item.name}`}
        >
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </button>
        <span className="absolute top-3 right-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 font-mono text-sm font-bold text-coffee">
          ₹{item.price}
        </span>
        {item.vegan ? (
          <span data-testid={`vegan-badge-${index}`} className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#2E7D32] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
            <Leaf size={12} /> Vegan
          </span>
        ) : item.vegetarian ? (
          <span data-testid={`veg-badge-${index}`} className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#B4671E] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
            <Sprout size={12} /> Vegetarian
          </span>
        ) : null}
        {hasGallery && (
          <span
            data-testid={`gallery-count-${index}`}
            className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-coffee/80 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white"
          >
            <Images size={12} /> {gallery.length} photos
          </span>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl font-bold text-coffee leading-tight">{item.name}</h3>

        <div className="mt-2 flex items-center gap-2" data-testid={`rating-${index}`}>
          <div className="flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map((s) => (
              <Star key={s} size={14} className={s < Math.round(rating) ? "fill-[#DE9A2C] text-[#DE9A2C]" : "text-line"} />
            ))}
          </div>
          <span className="font-mono text-sm font-bold text-coffee">{rating.toFixed(1)}</span>
          <span className="text-xs text-coffee-muted">({reviews} reviews)</span>
        </div>

        {goals?.protein > 0 && (
          <div data-testid={`goal-fit-${index}`} className="mt-2 inline-flex items-center gap-1.5 self-start rounded-full bg-[#DE9A2C]/15 text-[#8A2231] px-3 py-1 text-xs font-bold">
            🎯 {Math.round((item.protein / goals.protein) * 100)}% of your protein goal
          </div>
        )}

        {/* Premier-style bold macro highlight */}
        <div data-testid={`macro-highlight-${index}`} className="mt-4 grid grid-cols-3 rounded-2xl bg-[#8A2231] text-white overflow-hidden shadow-sm">
          <div className="px-2 py-3.5 text-center">
            <div className="font-display text-2xl sm:text-3xl font-black leading-none">{item.protein}<span className="text-base align-top">g</span></div>
            <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white/70">Protein</div>
          </div>
          <div className="px-2 py-3.5 text-center border-x border-white/15">
            <div className="font-display text-2xl sm:text-3xl font-black leading-none">{item.calories}</div>
            <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white/70">Calories</div>
          </div>
          <div className="px-2 py-3.5 text-center">
            <div className="font-display text-2xl sm:text-3xl font-black leading-none">0<span className="text-base align-top">g</span></div>
            <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-white/70">Added Sugar</div>
          </div>
        </div>

        <p className="mt-3 flex items-center gap-1.5 text-sm text-teal-brand"><Clock size={14} /> {item.bestFor}</p>

        <button
          type="button"
          data-testid={`macros-toggle-${index}`}
          onClick={() => setShowMacros((s) => !s)}
          aria-expanded={showMacros}
          className="mt-4 flex w-full items-center justify-between rounded-xl border border-line px-4 py-2.5 text-sm font-semibold text-coffee hover:border-teal-brand transition-colors"
        >
          Full nutrition &amp; ingredients
          <ChevronDown size={16} className={`transition-transform duration-300 ${showMacros ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence initial={false}>
          {showMacros && (
            <motion.div
              key="macros"
              data-testid={`macros-panel-${index}`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2.5 border-y border-line py-4">
                <div className="col-span-2 flex items-center gap-2">
                  <Flame size={16} className="text-teal-brand" />
                  <span className="text-xs text-coffee-muted uppercase tracking-wide">Calories</span>
                  <span className="ml-auto font-mono text-lg font-bold text-coffee" data-testid={`calories-${index}`}>{item.calories} kcal</span>
                </div>
                <MacroCell icon={Dumbbell} label="Protein" value={item.protein} unit="g" />
                <MacroCell icon={Wheat} label="Carbs" value={item.carbs} unit="g" />
                <MacroCell icon={Droplet} label="Fat" value={item.fat} unit="g" />
                <MacroCell icon={Candy} label="Sugar" value={item.sugar} unit="g" />
                {item.fiber !== undefined && <MacroCell icon={Leaf} label="Fiber" value={item.fiber} unit="g" />}
                <MacroCell icon={Sparkles} label="Sodium" value={item.sodium} unit="mg" />
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-coffee-soft">
                {item.allergens && <p><span className="font-semibold">Allergens:</span> {item.allergens}</p>}
                <p><span className="font-semibold">{item.ingredients ? "Ingredients:" : "Contains:"}</span> {item.ingredients || item.contains}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1" />

        <div className="mt-5 flex flex-wrap gap-2">
          {ADDONS.map((a) => {
            const on = selected.includes(a.name);
            return (
              <button
                key={a.name}
                data-testid={`addon-${index}-${a.name.split(" ")[0]}`}
                onClick={() => toggle(a.name)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  on ? "bg-[#8A2231] text-white border-teal-brand" : "border-line text-coffee-soft hover:border-teal-brand"
                }`}
              >
                {on ? <Check size={13} /> : <Plus size={13} />} {a.name.split(" +")[0]} (+₹{a.price})
              </button>
            );
          })}
        </div>

        <button
          data-testid={`add-to-cart-${index}`}
          onClick={() => {
            add({ key: `${item.name}|${[...selected].sort().join(",")}`, name: item.name, price: item.price + addonTotal, calories: item.calories, protein: item.protein, addons: selected });
            toast.success(`${item.name} added`, { description: `₹${item.price + addonTotal}${addonTotal ? " incl. add-ons" : ""}` });
          }}
          className="mt-5 w-full rounded-full bg-coffee hover:bg-[#8A2231] text-white font-semibold py-3 transition-colors"
        >
          Add to Cart · ₹{item.price + addonTotal}
        </button>
      </div>

      <AnimatePresence>
        {lbIndex >= 0 && (
          <motion.div
            data-testid={`lightbox-${index}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLb}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
          >
            <button
              data-testid={`lightbox-close-${index}`}
              onClick={(e) => { e.stopPropagation(); closeLb(); }}
              className="absolute top-5 right-5 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Close gallery"
            >
              <X size={22} />
            </button>

            <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 text-center">
                <p className="font-display text-xl font-bold text-white">{item.name}</p>
                <p className="font-mono text-xs text-white/60">{lbIndex + 1} / {gallery.length}</p>
              </div>

              <motion.img
                key={lbIndex}
                src={gallery[lbIndex]}
                alt={`${item.name} photo ${lbIndex + 1}`}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25 }}
                className="mx-auto max-h-[70vh] w-full rounded-2xl object-contain"
              />

              {hasGallery && (
                <>
                  <button
                    data-testid={`lightbox-prev-${index}`}
                    onClick={() => step(-1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
                    aria-label="Previous photo"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    data-testid={`lightbox-next-${index}`}
                    onClick={() => step(1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
                    aria-label="Next photo"
                  >
                    <ChevronRight size={24} />
                  </button>
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {gallery.map((_, d) => (
                      <button
                        key={d}
                        onClick={() => setLbIndex(d)}
                        className={`h-2 rounded-full transition-all ${d === lbIndex ? "w-6 bg-[#DE9A2C]" : "w-2 bg-white/40 hover:bg-white/70"}`}
                        aria-label={`Go to photo ${d + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}
